import { DataSignal } from 's-js';
import * as $ from 'jquery';
import * as _ from 'underscore';
import { ITypeaheadServerSide, IRequestQueue } from './typeahead-server-side';
import 'lib/typeahead.jquery';

/**
 * Construct a wrapper object for interfacing between a typeahead.js typeahead, S and React.
 * 
 * See https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md
 * 
 * PARAMETERS
 * options -- {Object} a hash with the following required properties:
 * 
 *     typeaheadOptions -- {Object} The hash of options passed as the first arg to the typeahead.js
 *                                initializer. See https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#options
 *     collection -- {Observable} An array-valued observable that gives the total list of possible
 *                                suggestion objects for the typeahead dropdown.
 *     data       -- {Observable} The string-valued observable to which the typeahead is bound.
 *                                The MakeTypeahead method will take care of updating this
 *                                observable when the user selects a suggestion. It will also
 *                                subscribe to this observable and clear the typeahead when the
 *                                observable is set to a falsy value.
 *                                NOTE: The MakeTypeahead constrcutor does NOT create a full two-way
 *                                binding between the typeahead and the observable. It does NOT
 *                                automatically update the observable as the user types. For this,
 *                                use the value and valueUpdate bindings in conjuction with a
 *                                STypeahead. It also does NOT automatically update the internal
 *                                value of the typeahead anytime the value of the observable changes.
 *                                Such a strong two-way binding would interfere with the normal
 *                                behavior of the typeahead. If you want, to directly set the
 *                                internal value of the typeahead (which affects which suggestions
 *                                will be shown in the dropdown), call typeahead api methods on
 *                                the $el property of the STypeahead object.
 *                                See https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#api
 *     matchFn -- {Function}  A callback used to perform the match between a suggestion object and
 *                            the current typeahead query. Expected to have the signature
 *                            (suggestion, query), where suggestion is a suggestion object (i.e.,
 *                            one of the elements of the collection observable) and query is a
 *                            string.
 *     displayKey -- {Function}  A callback passed directly as the displayKey property of the
 *                               typeahead's single dataset. See https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#datasets
 * 
 * RETURNS
 * An Typeahead viewmodel which can be passed to <TypeaheadInput model = {model} /> for rendering.
 * 
 * USAGE
 * Typeahead is intended to create the viewmodel used by the view <TypeaheadInput model={model} />
 * 
 * In your JS, construct an Typeahead and expose it to your view model as, say, typeahead.
 * 
 * let typeahead = new Typeahead({
 *  data: <data signal>,
 *  collection: <signal for array of items>,
 *  <etc...>
 * })
 * 
 * Then, in your view, do
 * <TypeaheadInput model={typeahead} />
 *  
 */
export interface ClientSideTypeaheadParams<T> {
    collection: () => T[], 
    matchFn: (item: T, query: string) => boolean, 
    typeaheadOptions?: any, 
    displayKey: (i : T) => string, 
    templates?: any, 
    observable: DataSignal<string>, 
    onSearchResults?: (r : T[]) => void,
    onSelect? : (i : T) => void
}

export interface ServerSideTypeaheadParams<T> {
    searchUrl : string, 
    typeaheadOptions?: any, 
    displayKey: (i : T) => string, 
    templates?: any, 
    observable: DataSignal<string>, 
    onSearchResults?: (r : T[]) => void
    serverSideSearch: ITypeaheadServerSide<T>,
    onSelect? : (i : T) => void
}

export class Typeahead<T> {
    searchUrl : string | null;
    typeaheadOptions: any; 
    displayKey: (i : T) => string; 
    templates: any;
    data: DataSignal<string>;
    collection: (() => T[]) | null;
    matchFn: ((item: T, query: string) => boolean) | null; 
    onSearchResults: ((r : T[]) => void) | null;
    serverSideSearch: ITypeaheadServerSide<T> | null; 
    onSelect: ((i : T) => void) | null;

    requestQueue : IRequestQueue<T> | null = null;

    constructor(options : ServerSideTypeaheadParams<T>);
    constructor(options : ClientSideTypeaheadParams<T>);
    constructor(options : {
        searchUrl? : string, 
        typeaheadOptions?: any, 
        displayKey: (i : T) => string, 
        templates?: any, 
        observable: DataSignal<string>, 
        collection?: () => T[], 
        matchFn?: (item: T, query: string) => boolean, 
        onSearchResults?: (r : T[]) => void
        serverSideSearch? : ITypeaheadServerSide<T>,
        onSelect? : (i : T) => void
    }) { 
        this.searchUrl = options.searchUrl || null;
        this.typeaheadOptions = options.typeaheadOptions;
        this.displayKey = options.displayKey;
        this.templates = options.templates;
        this.data = options.observable;
        this.collection = options.collection || null;
        this.matchFn = options.matchFn || null;
        this.onSearchResults = options.onSearchResults || null;
        this.serverSideSearch = options.serverSideSearch || null;
        this.onSelect = options.onSelect || null;
        
        // if searchUrl option is present, do server-side search instead of client-side
        if (this.searchUrl && !!this.serverSideSearch) {
            var apiConventions = this.serverSideSearch.ApiConventions(this.searchUrl);
            this.requestQueue = this.serverSideSearch.RequestQueue(apiConventions);
        }
    }
}


// WEBPACK FOOTER //
// ./apps/portcall/typeahead/model.ts